<script setup>
import {ref, computed} from 'vue';
import BetCalcSelectionOdds from "@/components/bet_calc/BetCalcSelectionOdds.vue";
import BetCalcBetType from "@/components/bet_calc/BetCalcBetType.vue";
import BetCalcStake from "@/components/bet_calc/BetCalcStake.vue";
import BetCalcOddsType from "@/components/bet_calc/BetCalcOddsType.vue";
import {getPlaceOdds} from "@/services/getPlaceOdds";

const stake = ref(10);
const oddsType = ref('decimal');
const betType = ref('qualifying');
const decimalOdds = ref('2');
const eachWayTerms = ref('1.2')


const result = computed(() => {
  const ewStake = stake.value / 2
  const returns = {win: null, place: null}
  const winOdds = parseFloat(decimalOdds.value)
  if (winOdds && !isNaN(winOdds)) {
    if (betType.value === 'qualifying') {
      returns.win = ewStake * winOdds
    } else {
      returns.win = ewStake * (winOdds - 1)
    }
  }
  const placeOdds = getPlaceOdds(winOdds, eachWayTerms.value)
  if (placeOdds && !isNaN(placeOdds)) {
    if (betType.value === 'qualifying') {
      returns.place = ewStake * placeOdds;
    } else {
      returns.place = ewStake * (placeOdds - 1);
    }
  }

  return returns


});

const updateDecimalOdds = (newDecimalOdds) => {
  decimalOdds.value = newDecimalOdds;
};

const getColorClass = (value) => {
  return value >= 0 ? 'positive' : 'negative';
};

// Function to format currency with a negative sign before the £ symbol
const formatCurrency = (value) => {
  return value < 0 ? `-£${Math.abs(value).toFixed(2)}` : `£${value.toFixed(2)}`;
};
</script>

<template>
  <div class="bet-calc-ew-bet-calculator">
    <BetCalcOddsType v-model="oddsType"/>
    <BetCalcBetType v-model="betType"/>
    <div class="ew-stake-odds">
      <BetCalcStake v-model="stake" label="Total Combined Stake"/>

      <BetCalcSelectionOdds
          :oddsType="oddsType"
          label="Odds:"
          @update:decimalOdds="updateDecimalOdds"
      />
    </div>
    <BetCalcSelectionOdds
        oddsType="fractional"
        label="Each Way Terms:"
        @update:decimalOdds="value => eachWayTerms = value"
        style="margin: 1.5rem 0.5rem"
        initial-denominator="5"
    />
    <div v-if="result.win && result.place">
      <table class="result-table">
        <thead>
        <tr>
          <th>Result</th>
          <th>Return (£)</th>
          <th>Profit (£)</th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td>Win</td>
          <td :class="getColorClass(result.win + result.place)">
            £{{ (result.win + result.place).toFixed(2) }}
          </td>
          <td :class="getColorClass(result.win + result.place - stake)">
            {{ formatCurrency(result.win + result.place - stake) }}
          </td>
        </tr>
        <tr>
          <td>Place</td>
          <td :class="getColorClass(result.place)">
            £{{ result.place.toFixed(2) }}
          </td>
          <td :class="getColorClass(result.place - stake)">
            {{ formatCurrency(result.place - stake) }}
          </td>
        </tr>
        <tr>
          <td>Total</td>
          <td>£0</td>
          <td :class="getColorClass(-stake)">
            {{ formatCurrency(-stake) }}
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.bet-calc-ew-bet-calculator {
  max-width: 576px;
  margin: 0 auto;
  padding: 20px;
  background-color: var(--clr-black-10);
}

.ew-stake-odds {
  display: flex;
  justify-content: space-between;
  margin: 1.5rem 0.5rem;
  gap: 2rem;

}

.ew-stake-odds > * {
  flex: 1;
}

.result-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.result-table th,
.result-table td {
  border: 1px solid #ccc;
  padding: 10px;
  text-align: center;
}

.result-table th {
  background-color: #f9f9f9;
  font-weight: bold;
}

.result-table td {
  font-size: 16px;
}

/* Positive values (greater than or equal to 0) in green */
.positive {
  color: green;
}

/* Negative values in red */
.negative {
  color: red;
}
</style>
